import { GameState } from '../core/GameState.js';

// ── Research Tree (18 items, 4 tiers) ─────────────────────────
// Costs are 10x their original values (2026-09-05 balance pass — research
// was too cheap relative to fleet/trade income; a further fine-tuning pass
// may adjust individual items from here rather than as a flat multiplier).
export const RESEARCH_TREE = {
  basic_navigation: {
    id: 'basic_navigation',
    name: 'Basic Navigation',
    description: 'Improved chart reading and route planning — +5% speed',
    tier: 1,
    cost: 350000,
    researchTime: 5,
    prerequisites: [],
    effects: { speedModifier: 1.05 }
  },
  cargo_handling: {
    id: 'cargo_handling',
    name: 'Cargo Handling',
    description: 'Better stowage techniques — +5% cargo capacity',
    tier: 1,
    cost: 450000,
    researchTime: 5,
    prerequisites: [],
    effects: { cargoCapacityModifier: 1.05 }
  },
  fuel_efficiency: {
    id: 'fuel_efficiency',
    name: 'Fuel Efficiency',
    description: 'Engine tuning and hull coatings — 8% fuel savings',
    tier: 1,
    cost: 500000,
    researchTime: 7,
    prerequisites: [],
    effects: { fuelConsumptionModifier: 0.92 }
  },
  market_awareness: {
    id: 'market_awareness',
    name: 'Market Awareness',
    description: 'Basic price intelligence — price cells show buy/sell hints',
    tier: 1,
    cost: 300000,
    researchTime: 4,
    prerequisites: [],
    effects: { unlocksFeature: 'market_hints' }
  },

  advanced_navigation: {
    id: 'advanced_navigation',
    name: 'Advanced Navigation',
    description: 'Weather routing and current analysis — +5% speed',
    tier: 2,
    cost: 1000000,
    researchTime: 10,
    prerequisites: ['basic_navigation'],
    effects: { speedModifier: 1.05 }
  },
  crew_navigation: {
    id: 'crew_navigation',
    name: 'Crew Navigation Training',
    description: 'Advanced navigator training programs — unlocks Senior & Expert navigators',
    tier: 2,
    cost: 900000,
    researchTime: 10,
    prerequisites: ['basic_navigation'],
    effects: { unlocksFeature: 'crew_navigation' }
  },
  cargo_optimization: {
    id: 'cargo_optimization',
    name: 'Cargo Optimization',
    description: 'Container stacking algorithms — +8% cargo capacity',
    tier: 2,
    cost: 1200000,
    researchTime: 12,
    prerequisites: ['cargo_handling'],
    effects: { cargoCapacityModifier: 1.08 }
  },
  ship_maintenance: {
    id: 'ship_maintenance',
    name: 'Ship Maintenance Program',
    description: 'Structured maintenance protocols — unlocks Senior & Expert engineers',
    tier: 2,
    cost: 1000000,
    researchTime: 10,
    prerequisites: ['fuel_efficiency'],
    effects: { unlocksFeature: 'ship_maintenance' }
  },
  logistics_efficiency: {
    id: 'logistics_efficiency',
    name: 'Logistics Efficiency',
    description: 'Port coordination and crane scheduling — 20% faster and cheaper cargo handling',
    tier: 2,
    cost: 1000000,
    researchTime: 10,
    prerequisites: ['cargo_handling', 'fuel_efficiency'],
    effects: { loadingTimeModifier: 0.8, loadingFeeModifier: 0.8 }
  },
  market_intelligence: {
    id: 'market_intelligence',
    name: 'Market Intelligence',
    description: 'Deep market analysis — price gradients show profit magnitude',
    tier: 2,
    cost: 800000,
    researchTime: 8,
    prerequisites: ['market_awareness'],
    effects: { unlocksFeature: 'market_gradients' }
  },
  market_analysis: {
    id: 'market_analysis',
    name: 'Market Analysis',
    description: 'Professional market research — unlocks Market Analyst hiring',
    tier: 2,
    cost: 800000,
    researchTime: 8,
    prerequisites: ['market_awareness'],
    effects: { unlocksFeature: 'market_analysis' }
  },

  expert_navigation: {
    id: 'expert_navigation',
    name: 'Expert Navigation',
    description: 'Satellite-assisted optimal routing — +5% speed',
    tier: 3,
    cost: 2000000,
    researchTime: 18,
    prerequisites: ['advanced_navigation'],
    effects: { speedModifier: 1.05 }
  },
  advanced_logistics: {
    id: 'advanced_logistics',
    name: 'Advanced Logistics',
    description: 'Automated port operations — 15% faster and cheaper cargo handling, 5% fuel savings',
    tier: 3,
    cost: 2500000,
    researchTime: 20,
    prerequisites: ['logistics_efficiency', 'cargo_optimization'],
    effects: { loadingTimeModifier: 0.85, loadingFeeModifier: 0.85, fuelConsumptionModifier: 0.95 }
  },

  autonomous_systems: {
    id: 'autonomous_systems',
    name: 'Autonomous Systems',
    description: 'AI-assisted bridge operations — 30% crew cost reduction, 5% fuel savings',
    tier: 4,
    cost: 5000000,
    researchTime: 35,
    prerequisites: ['expert_navigation', 'advanced_logistics'],
    effects: { crewCostModifier: 0.7, fuelConsumptionModifier: 0.95 }
  },

  warehouse_operations: {
    id: 'warehouse_operations',
    name: 'Warehouse Operations',
    description: 'Port-side cargo storage — unlocks warehouse construction at ports',
    tier: 3,
    cost: 3000000,
    researchTime: 25,
    prerequisites: ['advanced_logistics', 'market_intelligence'],
    effects: { unlocksFeature: 'warehouses' }
  },
  basic_automation: {
    id: 'basic_automation',
    name: 'Basic Trade Automation',
    description: 'Automated trade routes with rule-based actions — hire junior & senior managers',
    tier: 3,
    cost: 5000000,
    researchTime: 30,
    prerequisites: ['advanced_logistics', 'market_intelligence'],
    effects: { unlocksFeature: 'trade_routes' }
  },
  advanced_automation: {
    id: 'advanced_automation',
    name: 'Advanced Trade Automation',
    description: 'AI-driven route optimization — hire expert managers for smart profit analysis',
    tier: 4,
    cost: 10000000,
    researchTime: 40,
    prerequisites: ['basic_automation', 'autonomous_systems'],
    effects: { unlocksFeature: 'smart_routes' }
  },
  warehouse_expansion: {
    id: 'warehouse_expansion',
    name: 'Warehouse Expansion',
    description: 'Advanced storage infrastructure — upgrade warehouses for larger berths and more capacity',
    tier: 4,
    cost: 4000000,
    researchTime: 30,
    prerequisites: ['warehouse_operations'],
    effects: { unlocksFeature: 'warehouse_upgrades' }
  }
};

// ── Permit Tree (9 items, 4 tiers) ───────────────────────────
export const PERMIT_TREE = {
  roro_certification: {
    id: 'roro_certification',
    name: 'RoRo Certification',
    description: 'Roll-on/roll-off vehicle handling certification',
    tier: 1,
    cost: 60000,
    researchTime: 10,
    prerequisites: [],
    effects: { unlocksShipType: 'roro' },
    bribeCost: 30000,
    bribeSuccessRate: 0.85,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },
  refrigeration_permit: {
    id: 'refrigeration_permit',
    name: 'Refrigeration Permit',
    description: 'Cold-chain handling and temperature-controlled cargo license',
    tier: 1,
    cost: 80000,
    researchTime: 14,
    prerequisites: [],
    effects: { unlocksShipType: 'reefer' },
    bribeCost: 40000,
    bribeSuccessRate: 0.80,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },

  express_license: {
    id: 'express_license',
    name: 'Express Cargo License',
    description: 'Fast-vessel certification for premium and time-sensitive cargo operations',
    tier: 2,
    cost: 150000,
    researchTime: 18,
    prerequisites: [],
    effects: { unlocksShipType: 'express_clipper' },
    bribeCost: 80000,
    bribeSuccessRate: 0.75,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },
  tanker_license: {
    id: 'tanker_license',
    name: 'Tanker License',
    description: 'Hazardous liquid cargo transport authorization',
    tier: 2,
    cost: 200000,
    researchTime: 25,
    prerequisites: [],
    effects: { unlocksShipType: 'coastal_tanker' },
    bribeCost: 100000,
    bribeSuccessRate: 0.70,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },
  large_vessel_permit: {
    id: 'large_vessel_permit',
    name: 'Large Vessel Permit',
    description: 'Authorization to operate vessels over 2000 TEU',
    tier: 2,
    cost: 250000,
    researchTime: 25,
    prerequisites: [],
    effects: { unlocksShipType: 'large_cargo' },
    bribeCost: 125000,
    bribeSuccessRate: 0.70,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },

  lng_handling: {
    id: 'lng_handling',
    name: 'LNG Certification',
    description: 'Cryogenic cargo handling and safety certification',
    tier: 3,
    cost: 500000,
    researchTime: 35,
    prerequisites: ['tanker_license'],
    effects: { unlocksShipType: 'lng_carrier' },
    bribeCost: 300000,
    bribeSuccessRate: 0.55,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },
  mega_vessel_auth: {
    id: 'mega_vessel_auth',
    name: 'Mega-Vessel Authorization',
    description: 'Engineering certification for 8000+ TEU vessel operation',
    tier: 3,
    cost: 800000,
    researchTime: 40,
    prerequisites: ['large_vessel_permit'],
    effects: { unlocksShipType: 'post_panamax' },
    bribeCost: 500000,
    bribeSuccessRate: 0.50,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },
  port_construction: {
    id: 'port_construction',
    name: 'Port Construction License',
    description: 'Authorization to build and operate port facilities',
    tier: 3,
    cost: 1000000,
    researchTime: 50,
    prerequisites: ['large_vessel_permit'],
    effects: { unlocksFeature: 'build_ports' },
    bribeCost: 600000,
    bribeSuccessRate: 0.45,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },

  ultra_large_vessels: {
    id: 'ultra_large_vessels',
    name: 'Ultra-Large Vessel Permit',
    description: 'Authorization for ULCV and Megamax-24 class operations',
    tier: 4,
    cost: 2000000,
    researchTime: 60,
    prerequisites: ['mega_vessel_auth'],
    effects: { unlocksShipType: 'ulcv' },
    bribeCost: 1200000,
    bribeSuccessRate: 0.35,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  },
  factory_construction: {
    id: 'factory_construction',
    name: 'Industrial Development',
    description: 'Authorization to build industrial facilities',
    tier: 4,
    cost: 2500000,
    researchTime: 70,
    prerequisites: ['port_construction'],
    effects: { unlocksFeature: 'build_factories' },
    bribeCost: 1500000,
    bribeSuccessRate: 0.30,
    bribeFineMultiplier: 2,
    bribeCooldown: 14
  }
};

// ── Combined lookup (for modifier calculations) ───────────────
export const ALL_UNLOCKS = { ...RESEARCH_TREE, ...PERMIT_TREE };

// ── Helpers ───────────────────────────────────────────────────
/** Check if a research or permit ID has been completed */
export function isUnlockCompleted(id) {
  if (!id) return true;
  return (GameState.research?.completed?.has(id) || GameState.permits?.completed?.has(id)) ?? false;
}

/** Check if a feature (e.g. 'market_hints', 'build_ports') is unlocked */
export function isFeatureUnlocked(featureId) {
  return isFeatureUnlockedFor(featureId, GameState.research?.completed, GameState.permits?.completed);
}

/**
 * Pure variant of `isFeatureUnlocked()` that takes explicit completed-research
 * and completed-permit Sets instead of reading the ambient `GameState`
 * singleton. Added for the server-side multiplayer runtime
 * (`server/game/systems/SharedCrewPool.js`): world-shared logic must not
 * depend on whichever player happens to be bound to `GameState` at the
 * moment it runs (research/permits are per-player state — see
 * docs/proposals/multiplayer-realtime-world.md, Phase 3). `isFeatureUnlocked()`
 * above is unchanged for every existing (single-player/browser) call site —
 * it now just delegates to this pure function using the ambient GameState,
 * so this is a behavior-preserving refactor for everyone except new callers
 * that explicitly want to pass their own Sets.
 *
 * @param {string} featureId
 * @param {Set<string>} [completedResearch]
 * @param {Set<string>} [completedPermits]
 */
export function isFeatureUnlockedFor(featureId, completedResearch, completedPermits) {
  if (!featureId) return false;
  for (const tech of Object.values(ALL_UNLOCKS)) {
    if (tech.effects?.unlocksFeature === featureId) {
      if (!tech.id) return true;
      if (completedResearch?.has(tech.id) || completedPermits?.has(tech.id)) return true;
    }
  }
  return false;
}

// ── Crew hiring gates (spec: Crew & Staff) ───────────────────
/** Research feature that gates each crew role. */
export const CREW_ROLE_RESEARCH = {
  navigator:      'crew_navigation',
  engineer:       'ship_maintenance',
  market_analyst: 'market_analysis'
};

/**
 * Why this player can't hire a crew member of this role/tier, or null if
 * they can. Navigators/engineers: junior always, senior/expert need the
 * role's research. Market analysts: any tier needs `market_analysis`.
 * Pure (explicit Sets) so the server can check the hiring player's own
 * research rather than whoever is bound to GameState.
 */
export function crewHireBlockedReason(role, tier, completedResearch, completedPermits) {
  const feature = CREW_ROLE_RESEARCH[role];
  if (!feature) return null;
  const gated = role === 'market_analyst' || tier !== 'junior';
  if (!gated || isFeatureUnlockedFor(feature, completedResearch, completedPermits)) return null;
  const tech = Object.values(ALL_UNLOCKS).find(t => t.effects?.unlocksFeature === feature);
  return `Requires ${tech?.name || feature} research`;
}

// ── Save migration map (old v1 IDs → new IDs + tree) ─────────
export const MIGRATION_MAP = {
  efficient_engines:        { tree: 'research', newId: 'fuel_efficiency' },
  better_navigation:        { tree: 'research', newId: 'basic_navigation' },
  cargo_optimization:       { tree: 'research', newId: 'cargo_handling' },
  refrigeration:            { tree: 'permits',  newId: 'refrigeration_permit' },
  tanker_operations:        { tree: 'permits',  newId: 'tanker_license' },
  advanced_logistics:       { tree: 'research', newId: 'logistics_efficiency' },
  lng_technology:           { tree: 'permits',  newId: 'lng_handling' },
  mega_vessel_construction: { tree: 'permits',  newId: 'mega_vessel_auth' },
  autonomous_systems:       { tree: 'research', newId: 'autonomous_systems' },
  ultra_large_vessels:      { tree: 'permits',  newId: 'ultra_large_vessels' },
  port_construction:        { tree: 'permits',  newId: 'port_construction' },
  factory_construction:     { tree: 'permits',  newId: 'factory_construction' }
};
